import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Button,
  Badge,
  Stack,
  Table,
  ActionIcon,
  Alert,
} from "@mantine/core";
import { IconArrowLeft, IconAward } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useRFQComparison } from "../hooks/useRFQ";
import { useAwardRFQ } from "../hooks/useRFQ";
import { useState } from "react";
import { Modal, Textarea, Select } from "@mantine/core";
import dayjs from "dayjs";

export function RFQComparison() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: comparison, isLoading } = useRFQComparison(id);
  const awardRFQ = useAwardRFQ();

  const [awardModalOpen, setAwardModalOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState<string>("");
  const [awardNotes, setAwardNotes] = useState("");

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading comparison...</Text>
      </Container>
    );
  }

  if (!comparison || !comparison.quotations || comparison.quotations.length === 0) {
    return (
      <Container size="xl" py="xl">
        <Group mb="xl">
          <ActionIcon variant="subtle" onClick={() => navigate(`/rfq/${id}`)}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2}>Quote Comparison</Title>
        </Group>
        <Alert color="yellow">No quotations available for comparison</Alert>
      </Container>
    );
  }

  const handleAward = () => {
    if (!selectedQuotation || !awardNotes.trim()) {
      return;
    }
    awardRFQ.mutate(
      {
        id: id!,
        data: {
          quotationId: selectedQuotation,
          notes: awardNotes,
        },
      },
      {
        onSuccess: () => {
          setAwardModalOpen(false);
          navigate(`/rfq/${id}`);
        },
      }
    );
  };

  const rankingColors: Record<string, string> = {
    L1: "green",
    L2: "blue",
    L3: "yellow",
  };

  const isAwarded = comparison?.rfq?.status === "AWARDED" || !!comparison?.rfq?.awardedQuotationId;

  return (
    <Container size="xl" py="xl">
      <Group mb="xl">
        <ActionIcon variant="subtle" onClick={() => navigate(`/rfq/${id}`)}>
          <IconArrowLeft size={20} />
        </ActionIcon>
        <Title order={2}>Quote Comparison</Title>
      </Group>

      <Stack gap="md">
        <Paper shadow="sm" p="md">
          <Group justify="space-between" mb="md">
            <Title order={4}>Summary</Title>
          </Group>
          <Stack gap="sm">
            <Group>
              <Text fw={500}>Total Quotations:</Text>
              <Text>{comparison.summary.totalQuotations}</Text>
            </Group>
            {comparison.summary.l1Vendor && (
              <Group>
                <Text fw={500}>L1 Vendor:</Text>
                <Text>{comparison.summary.l1Vendor}</Text>
                <Text fw={500}>L1 Price:</Text>
                <Text>
                  {comparison.summary.l1Price
                    ? `${comparison.rfq.currency} ${Number(comparison.summary.l1Price).toFixed(2)}`
                    : "-"}
                </Text>
              </Group>
            )}
            {comparison.summary.priceRange.min && (
              <Group>
                <Text fw={500}>Price Range:</Text>
                <Text>
                  {comparison.rfq.currency}{" "}
                  {Number(comparison.summary.priceRange.min).toFixed(2)} -{" "}
                  {comparison.rfq.currency}{" "}
                  {comparison.summary.priceRange.max ? Number(comparison.summary.priceRange.max).toFixed(2) : "-"}
                </Text>
              </Group>
            )}
            {comparison.summary.averagePrice && (
              <Group>
                <Text fw={500}>Average Price:</Text>
                <Text>
                  {comparison.rfq.currency}{" "}
                  {Number(comparison.summary.averagePrice).toFixed(2)}
                </Text>
              </Group>
            )}
          </Stack>
        </Paper>

        <Paper shadow="sm" p="md">
          <Title order={4} mb="md">
            Side-by-Side Comparison
          </Title>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ranking</Table.Th>
                <Table.Th>Vendor</Table.Th>
                <Table.Th>Total Price</Table.Th>
                <Table.Th>Delivery Days</Table.Th>
                <Table.Th>Validity Days</Table.Th>
                <Table.Th>Payment Terms</Table.Th>
                <Table.Th>Submitted At</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {comparison.quotations.map((quotation: any) => (
                <Table.Tr
                  key={quotation.id}
                  style={{
                    backgroundColor:
                      quotation.ranking === "L1"
                        ? "rgba(34, 197, 94, 0.1)"
                        : quotation.ranking === "L2"
                        ? "rgba(59, 130, 246, 0.1)"
                        : quotation.ranking === "L3"
                        ? "rgba(234, 179, 8, 0.1)"
                        : undefined,
                  }}
                >
                  <Table.Td>
                    <Badge
                      color={rankingColors[quotation.ranking] || "gray"}
                      variant="light"
                      size="lg"
                    >
                      {quotation.ranking}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {quotation.vendor.businessName || quotation.vendor.email}
                  </Table.Td>
                  <Table.Td>
                    <Text fw={500}>
                      {quotation.currency} {Number(quotation.totalPrice).toFixed(2)}
                    </Text>
                  </Table.Td>
                  <Table.Td>{quotation.deliveryDays} days</Table.Td>
                  <Table.Td>{quotation.validityDays} days</Table.Td>
                  <Table.Td>{quotation.paymentTerms || "-"}</Table.Td>
                  <Table.Td>
                    {quotation.submittedAt
                      ? dayjs(quotation.submittedAt).format("DD MMM YYYY")
                      : "-"}
                  </Table.Td>
                  <Table.Td>
                    {!isAwarded && (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => {
                          setSelectedQuotation(quotation.id);
                          setAwardModalOpen(true);
                        }}
                      >
                        Award
                      </Button>
                    )}
                    {isAwarded && quotation.id === comparison?.rfq?.awardedQuotationId && (
                      <Badge color="green" variant="light">
                        Awarded
                      </Badge>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>

        <Group justify="flex-end">
          <Button variant="subtle" onClick={() => navigate(`/rfq/${id}`)}>
            Back to RFQ
          </Button>
          {!isAwarded && (
            <Button
              leftSection={<IconAward size={16} />}
              onClick={() => {
                if (comparison.quotations.length > 0) {
                  setSelectedQuotation(comparison.quotations[0].id);
                  setAwardModalOpen(true);
                }
              }}
              disabled={comparison.quotations.length === 0}
            >
              Award L1 Vendor
            </Button>
          )}
        </Group>
      </Stack>

      <Modal
        opened={awardModalOpen}
        onClose={() => setAwardModalOpen(false)}
        title="Award RFQ"
        size="lg"
      >
        <Stack>
          <Select
            label="Select Quotation"
            placeholder="Choose quotation to award"
            data={comparison.quotations.map((q: any) => ({
              value: q.id,
              label: `${q.ranking} - ${q.vendor.businessName || q.vendor.email} - ${q.currency} ${Number(q.totalPrice).toFixed(2)}`,
            }))}
            value={selectedQuotation}
            onChange={(value) => setSelectedQuotation(value || "")}
          />
          <Textarea
            label="Selection Notes"
            placeholder="Enter notes explaining the selection (required)"
            required
            minRows={3}
            value={awardNotes}
            onChange={(e) => setAwardNotes(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAwardModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAward}
              loading={awardRFQ.isPending}
              disabled={!selectedQuotation || !awardNotes.trim()}
              color="green"
            >
              Award RFQ
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

